import Axios from "axios";
import toast from "react-hot-toast";
import i18n from "../i18n/i18n";

/**
 * Serialise query parameters the way the API's validator reads them.
 *
 * THE BUG THIS EXISTS TO PREVENT
 * ------------------------------
 * Axios' default serialiser writes a JS boolean as the word `true`, so
 * `params: { include_inactive: true }` goes out as `?include_inactive=true`.
 * A query string has no types, so PHP receives the STRING "true" - and Laravel's
 * `boolean` rule accepts exactly six values: true, false, 1, 0, "1", "0".
 * "true" is not among them.
 *
 * The result was a hard 422 on GET /pub for every single request the pub screen
 * made:
 *
 *     { "include_inactive": ["Le champ include inactive doit être vrai ou faux."] }
 *
 * ...on a request that was semantically fine. The list rendered "Aucune donnée"
 * against a populated catalogue, which reads as an empty database rather than a
 * rejected request.
 *
 * `1` / `0` are accepted by BOTH Laravel's `boolean` rule and PHP's
 * filter_var(FILTER_VALIDATE_BOOLEAN) that the services use, so emitting them is
 * the one spelling that is correct at every layer.
 *
 * The API was fixed too (App\Traits\NormalizesQueryBooleans coerces the string
 * forms before validation), and that is the real contract - this serialiser is
 * defence in depth for every OTHER endpoint in the admin that has not been
 * audited yet.
 *
 * It also drops `undefined`, `null` and `""`. Axios omits `undefined` but sends
 * `?city=` for an empty string, and an empty-but-present parameter is not the
 * same request as an absent one: a `nullable|integer` rule fails on `""`, and a
 * service that checks `isset()` sees a filter that the operator never set. An
 * unset filter should not appear in the URL at all.
 *
 * Arrays are emitted as repeated keys (`sign_ids[]=1&sign_ids[]=2`), which is
 * what PHP parses back into an array - the shape `sign_ids.*` is validated
 * against.
 */
const serializeParams = (params: Record<string, unknown>): string => {
  const search = new URLSearchParams();

  const append = (key: string, value: unknown): void => {
    if (value === undefined || value === null || value === "") return;

    if (typeof value === "boolean") {
      search.append(key, value ? "1" : "0");
      return;
    }

    if (value instanceof Date) {
      search.append(key, value.toISOString());
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => append(`${key}[]`, item));
      return;
    }

    if (typeof value === "object") {
      // Nested objects have no agreed encoding here; JSON keeps them at least
      // round-trippable instead of stringifying to "[object Object]".
      search.append(key, JSON.stringify(value));
      return;
    }

    search.append(key, String(value));
  };

  Object.entries(params ?? {}).forEach(([key, value]) => append(key, value));

  return search.toString();
};

const axios = Axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  timeout: Number(process.env.REACT_APP_REQUEST_TIMEOUT) || 60000,
  paramsSerializer: {
    serialize: serializeParams,
  },
});

axios.interceptors.request.use((config) => {
  // Read token for anywhere, in this case directly from localStorage
  const token = localStorage.getItem("token");
  if (token) {
    config.headers["X-token-api"] = token;
  }
  const lang = localStorage.getItem("lang");
  if (lang) {
    // use config.params if it has been set
    config.params = config.params || {};
    // add any client instance specific params to config
    config.params["lang"] = lang;
  }

  return config;
});

axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Safe access: message may be absent (HTML error page, network failure, blob)
    const status = error?.response?.status;
    const message = error?.response?.data?.message ?? "";

    const isSessionExpired =
      status === 401 ||
      (typeof message === "string" &&
        message.includes("DatabaseSessionUserProvider"));

    // Never force-logout on the login request itself (401 = wrong credentials)
    const isLoginRequest = error?.config?.url?.includes("/login");

    if (isSessionExpired && !isLoginRequest && localStorage.getItem("token")) {
      toast.error(
        i18n.t("login.sessionExpired", "Session expired, please login again")
      );
      localStorage.clear();
      window?.location?.reload();
    }

    /*
     * 403 is authorisation, not authentication. The API Gates deliberately
     * return 403 (see the note in AuthServiceProvider: they were changed from
     * 401 precisely so the SPA would stop treating a permission failure as a
     * dead session). Surfacing it explicitly means an operator who reaches a
     * guarded action gets "you don't have the rights" instead of silence.
     */
    if (status === 403 && !isLoginRequest) {
      toast.error(
        error?.response?.data?.message ||
          i18n.t(
            "common.forbidden",
            "Action non autorisée pour votre compte."
          )
      );
    }

    return Promise.reject(error);
  }
);

export default axios;
